import React from 'react';

const MindsetMovement = () => {
  return (
    <section id="break-free" className="min-h-screen flex items-center py-20 px-4 bg-gradient-to-b from-darker via-dark to-darker">
      <div className="container mx-auto">
        <div className="max-w-6xl mx-auto">
          {/* Section Title */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 font-heading">
              This Matrix <span className="bg-gradient-to-r from-danger via-warning to-accent bg-clip-text text-transparent">Must Break</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Not by rebellion, but by <span className="text-accent font-semibold">awareness</span>
            </p>
          </div>

          {/* The Realizations */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="bg-gradient-to-br from-primary/10 to-transparent border-2 border-primary/30 rounded-3xl p-8 hover:border-primary/50 transition-all duration-300">
              <div className="text-5xl mb-4">💙</div>
              <h4 className="text-2xl font-bold text-primary mb-4 font-heading">Love ≠ Control</h4>
              <p className="text-gray-300 leading-relaxed">
                By realizing that <span className="text-white font-semibold">love doesn't mean control</span>. True love means giving freedom to grow.
              </p>
            </div>

            <div className="bg-gradient-to-br from-accent/10 to-transparent border-2 border-accent/30 rounded-3xl p-8 hover:border-accent/50 transition-all duration-300">
              <div className="text-5xl mb-4">📈</div>
              <h4 className="text-2xl font-bold text-accent mb-4 font-heading">Failure = Growth</h4>
              <p className="text-gray-300 leading-relaxed">
                By understanding that <span className="text-white font-semibold">failure is not shameful</span> — it's part of growth and learning.
              </p>
            </div>

            <div className="bg-gradient-to-br from-secondary/10 to-transparent border-2 border-secondary/30 rounded-3xl p-8 hover:border-secondary/50 transition-all duration-300">
              <div className="text-5xl mb-4">🌱</div>
              <h4 className="text-2xl font-bold text-secondary mb-4 font-heading">Freedom To Explore</h4>
              <p className="text-gray-300 leading-relaxed">
                By letting <span className="text-white font-semibold">young minds breathe, explore, and take decisions</span> — even wrong ones — on their own.
              </p>
            </div>
          </div>

          {/* CS Student Perspective */}
          <div className="mb-16 bg-gradient-to-br from-dark/80 to-darker/80 backdrop-blur-sm border-2 border-primary/30 rounded-3xl p-10 md:p-12">
            <div className="flex items-start gap-6">
              <div className="text-5xl">💻</div>
              <div>
                <h3 className="text-3xl font-bold text-primary mb-6 font-heading">A Computer Science Student's Perspective</h3>
                <div className="space-y-4 text-lg md:text-xl text-gray-300 leading-relaxed">
                  <p>
                    As a <span className="text-primary font-semibold">computer science student</span>, I believe:
                  </p>
                  <p className="text-2xl text-white font-semibold">
                    Real change begins in <span className="text-accent">mindset</span>, not in <span className="text-secondary">machines</span>.
                  </p>
                  <p>
                    Technology can help — but it <span className="text-warning font-semibold">can't fix a society that refuses to think freely</span>.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* The Vision */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-accent/20 via-primary/20 to-secondary/20 border-2 border-accent/40 rounded-3xl p-10 md:p-14 text-center">
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-8 font-heading">
                When We Break Free...
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">✨</span>
                    <div>
                      <h5 className="text-xl font-bold text-accent mb-2">Freedom of Thought</h5>
                      <p className="text-gray-300">When we start allowing freedom of thought and exploration</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">🎯</span>
                    <div>
                      <h5 className="text-xl font-bold text-primary mb-2">Decision Making</h5>
                      <p className="text-gray-300">Young people can take their own decisions without fear</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">🚀</span>
                    <div>
                      <h5 className="text-xl font-bold text-secondary mb-2">From Followers to Creators</h5>
                      <p className="text-gray-300">Our people will stop being followers and start being creators</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">💡</span>
                    <div>
                      <h5 className="text-xl font-bold text-warning mb-2">Real Innovation</h5>
                      <p className="text-gray-300">We'll build products and companies that change the world</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Compare: Trapped vs Free */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-white mb-8 text-center font-heading">
              Two Different Paths
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Trapped Mind */}
              <div className="bg-gradient-to-br from-danger/10 to-transparent border-2 border-danger/40 rounded-3xl p-8">
                <h4 className="text-2xl font-bold text-danger mb-6 text-center">🔒 Trapped Mind</h4>
                <div className="space-y-3 text-gray-400">
                  <div className="flex items-center gap-3">
                    <span className="text-danger">✗</span>
                    <span>Follows predetermined path</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-danger">✗</span>
                    <span>Fears what society thinks</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-danger">✗</span>
                    <span>Avoids risk at all costs</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-danger">✗</span>
                    <span>Dependent on parents' approval</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-danger">✗</span>
                    <span>Lives others' dreams, not their own</span>
                  </div>
                </div>
              </div>

              {/* Free Mind */}
              <div className="bg-gradient-to-br from-accent/10 to-transparent border-2 border-accent/40 rounded-3xl p-8 shadow-lg shadow-accent/10">
                <h4 className="text-2xl font-bold text-accent mb-6 text-center">🔓 Free Mind</h4>
                <div className="space-y-3 text-gray-300">
                  <div className="flex items-center gap-3">
                    <span className="text-accent">✓</span>
                    <span className="font-semibold">Creates their own path</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-accent">✓</span>
                    <span className="font-semibold">Questions and explores</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-accent">✓</span>
                    <span className="font-semibold">Learns from failures</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-accent">✓</span>
                    <span className="font-semibold">Makes independent decisions</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-accent">✓</span>
                    <span className="font-semibold">Lives authentically</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Note */}
          <div className="bg-gradient-to-br from-primary/10 via-accent/5 to-secondary/10 border-2 border-primary/30 rounded-3xl p-10 text-center">
            <div className="text-5xl mb-6">🌍</div>
            <h3 className="text-3xl font-bold text-white mb-6 font-heading">
              Until Our Minds Are Free
            </h3>
            <p className="text-2xl text-gray-300 leading-relaxed mb-4">
              No amount of <span className="text-primary font-semibold">economic progress</span>,<br />
              no amount of <span className="text-secondary font-semibold">technological advancement</span>,<br />
              <span className="text-warning font-bold text-3xl">will ever be real</span>.
            </p>
            <p className="text-xl text-gray-400 mt-6 italic">
              Because innovation comes from free minds, not controlled ones.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MindsetMovement;